import { request } from 'app/utils';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';
import { AxiosResponse } from 'axios';
import { Flex, Hr, Loading, Page, RichContent, Typography } from 'components';
import { ProductDetailsApiResponse, useProducts } from 'features/products';
import { RecommendationsDetailsState } from 'features/recommendations';
import useRecommendations from 'features/recommendations/useRecommendations';
import { useSchool } from 'features/school';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

const Details = () => {
  const [pending, setPending] = useState(true);

  const { productSlug, productClassUuid, recommendationUuid } = useParams();
  const { currentTheme } = useTheme();

  const { recommendationsDetails, setRecommendationsDetails, clearRecommendationsDetails } = useRecommendations();
  const { apiGatewayUrl } = useSchool();
  const { productDetails, setProductDetails, clearProductDetails } = useProducts();

  const fetch = useCallback(async () => {
    setPending(true);

    await Promise.all([
      request({
        url: `/api/v1/School/Products/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}` : ``}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then((response: AxiosResponse<ProductDetailsApiResponse>) => {
        setProductDetails(response.data);
      }),
      request({
        url: `/api/v1/School/ProductContentRecommendationItem/${productSlug}/${recommendationUuid}`,
        method: 'get',
        baseURL: apiGatewayUrl,
        params: {
          productClassUuid,
        },
      }).then((response: AxiosResponse<RecommendationsDetailsState>) => {
        setRecommendationsDetails(response.data);
      }),
    ]).finally(() => {
      setPending(false);
    });
  }, [productSlug, productClassUuid, recommendationUuid]);

  useEffect(() => {
    fetch();

    return () => {
      clearProductDetails();
      clearRecommendationsDetails();
    };
  }, [productSlug, productClassUuid, recommendationUuid]);

  if (pending) return <Loading label="Por favor, aguarde." />;

  return (
    <Page title={recommendationsDetails?.title}>
      <Flex p={{ m: 30, d: 60 }} bg="articleBg">
        <Flex maxWidth={{ d: '65%' }} flexDirection="column">
          <Typography type="type3" color="color80">
            {productDetails?.product?.name}
            {' • '}
            Criado em: {formatDateFromUtcToTimezone(recommendationsDetails?.createdAt).format('DD/MM/YYYY')}
          </Typography>

          <Typography type="type6" fontWeight="bold" mt={{ m: 1, d: 2 }} color="color100">
            {recommendationsDetails?.title}
          </Typography>

          <Hr my={{ m: 3, d: 5 }} color="articleLine" />

          <Flex flexWrap="nowrap" flexDirection={{ m: 'column-reverse', t: 'row' }}>
            <Flex flexGrow={1}>
              <RichContent defaultType="type3" currentTheme={currentTheme}>
                {recommendationsDetails?.strategy}
              </RichContent>
            </Flex>

            <Flex flexGrow={0} width={{ t: 300 }} ml={{ t: 40, d: 70 }} mb={{ m: 4, t: 0 }} alignItems="flex-start" flexShrink={0}>
              <Flex bg="dark_350" borderRadius={4} flexDirection="column">
                {recommendationsDetails?.fields.map((field, index) => {
                  return (
                    <Flex key={`recommendation-field-item-${index.toString()}`} flexDirection="column">
                      {!!index && <Hr color="dark_150" m={0} />}

                      <Flex flexDirection="column" py={3} px={4}>
                        <Typography color="gray_500" type="type1">
                          {field.title}
                        </Typography>
                        <Typography color="white" mt={1} fontWeight="bold">
                          {field.contents}
                        </Typography>
                      </Flex>
                    </Flex>
                  );
                })}

                {!!recommendationsDetails?.comments && (
                  <>
                    <Hr color="dark_150" m={0} />

                    <Flex flexDirection="column" py={3} px={4}>
                      <RichContent defaultType="type1" currentTheme="dark">
                        {recommendationsDetails?.comments}
                      </RichContent>
                    </Flex>
                  </>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Page>
  );
};

export default Details;
